import React, { useEffect, useRef, useState, useCallback } from 'react';
import flatpickr from 'flatpickr';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import 'flatpickr/dist/flatpickr.min.css';
import * as styles from './DatePicker.module.css';
import Tabs from '../ui/Tabs';

export type PeriodType = 'day' | 'week' | 'month' | 'custom';

const PERIOD_LABELS: Record<PeriodType, string> = {
  day: 'День',
  week: 'Неделя',
  month: 'Месяц',
  custom: 'Произвольный',
};

const DEFAULT_PERIODS: PeriodType[] = ['week', 'month', 'custom'];

export interface DatePickerProps {
  dateFrom: string;
  dateTo: string;
  onDateChange: (dateFrom: string, dateTo: string) => void;
  /** Начальный период и переключение при изменении пропса. */
  period?: PeriodType;
  /** Вызывается при выборе другого периода пользователем. */
  onPeriodChange?: (period: PeriodType) => void;
  defaultPeriod?: PeriodType;
  periods?: PeriodType[];
  excludeToday?: boolean;
  allowFuture?: boolean;
  allowPast?: boolean;
  storageKeyPrefix?: string;
  loadSavedDates?: boolean;
}

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateLocal = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getCurrentWeek = (excludeToday: boolean = false) => {
  const today = new Date();
  if (excludeToday) today.setDate(today.getDate() - 1);
  const dayOfWeek = today.getDay(); // 0 = воскресенье, 1 = понедельник, ..., 6 = суббота
  // Вычисляем количество дней до понедельника
  // Если сегодня воскресенье (0), то понедельник был 6 дней назад
  // Если сегодня понедельник (1), то понедельник сегодня (0 дней назад)
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const monday = new Date(today);
  monday.setDate(today.getDate() + daysToMonday);

  return { from: formatDateLocal(monday), to: formatDateLocal(today) };
};

const isValidDate = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  formatDateLocal(parseDateLocal(value)) === value;

const getStorageKeys = (prefix?: string) => {
  const basePeriod = 'datepicker_period';
  const baseDates = 'datepicker_dates';

  if (prefix) {
    return {
      period: `${prefix}_${basePeriod}`,
      dates: `${prefix}_${baseDates}`,
    };
  }

  return {
    period: basePeriod,
    dates: baseDates,
  };
};

type SavedDates = {
  day?: { from: string; to: string };
  week?: { from: string; to: string };
  month?: { from: string; to: string };
  custom?: { from: string; to: string };
};

const getSavedDates = (storageKeyDates: string): SavedDates => {
  try {
    const saved = localStorage.getItem(storageKeyDates);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      const result: SavedDates = {};
      for (const key of Object.keys(PERIOD_LABELS) as PeriodType[]) {
        const range = parsed[key];
        if (
          range &&
          isValidDate(range.from) &&
          isValidDate(range.to) &&
          range.from <= range.to
        ) {
          result[key] = { from: range.from, to: range.to };
        }
      }
      return result;
    }
  } catch (e) {
    console.warn('Failed to load dates from localStorage', e);
  }
  return {};
};

const saveDatesForPeriod = (
  storageKeyDates: string,
  period: PeriodType,
  from: string,
  to: string
) => {
  try {
    const savedDates = getSavedDates(storageKeyDates);
    savedDates[period] = { from, to };
    localStorage.setItem(storageKeyDates, JSON.stringify(savedDates));
  } catch (e) {
    console.warn('Failed to save dates to localStorage', e);
  }
};

const getSavedDatesForPeriod = (
  storageKeyDates: string,
  period: PeriodType
): { from: string; to: string } | null => {
  const savedDates = getSavedDates(storageKeyDates);
  return savedDates[period] || null;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  dateFrom,
  dateTo,
  onDateChange,
  period,
  onPeriodChange,
  defaultPeriod = 'week',
  periods = DEFAULT_PERIODS,
  excludeToday = true,
  allowFuture = false,
  allowPast = false,
  storageKeyPrefix,
  loadSavedDates = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const flatpickrRef = useRef<flatpickr.Instance | null>(null);
  const previousPeriodRef = useRef(period);

  const storageKeys = getStorageKeys(storageKeyPrefix);

  // Явный период имеет приоритет над localStorage и defaultPeriod.
  const availablePeriods = periods.length ? periods : DEFAULT_PERIODS;

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>(() => {
    if (period && availablePeriods.includes(period)) {
      return period;
    }

    try {
      const saved = localStorage.getItem(storageKeys.period);
      if (saved && availablePeriods.includes(saved as PeriodType)) {
        return saved as PeriodType;
      }
    } catch {
      // Ignore
    }

    if (availablePeriods.includes(defaultPeriod)) {
      return defaultPeriod;
    }

    return availablePeriods[0];
  });

  const appliedPeriodRef = useRef(selectedPeriod);
  const appliedDatesRef = useRef({ from: dateFrom, to: dateTo });
  const externalTransitionRef = useRef(false);
  const externalPeriodChanged = previousPeriodRef.current !== period;
  const pendingDatesRef = useRef<{ from: string; to: string } | null>(null);
  const initializedStorageRef = useRef<string | null>(null);

  const normalizeDates = useCallback(
    (from: string, to: string) => {
      if (!isValidDate(from) || !isValidDate(to) || from > to) return null;
      const today = new Date();
      const minimum = formatDateLocal(today);
      if (excludeToday) today.setDate(today.getDate() - 1);
      const maximum = formatDateLocal(today);
      // Сохраняем прежнюю семантику allowPast: нижняя граница применяется
      // только при включенном выборе будущих дат.
      if (!allowPast && allowFuture) {
        from = from < minimum ? minimum : from;
        to = to < minimum ? minimum : to;
      }
      if (!allowFuture) {
        from = from > maximum ? maximum : from;
        to = to > maximum ? maximum : to;
      }
      return { from, to };
    },
    [allowFuture, allowPast, excludeToday]
  );

  // Пропс только синхронизирует внутреннее состояние.
  useEffect(() => {
    previousPeriodRef.current = period;
    if (externalPeriodChanged && period && availablePeriods.includes(period)) {
      if (period !== selectedPeriod) externalTransitionRef.current = true;
      setSelectedPeriod(period);
    } else if (!availablePeriods.includes(selectedPeriod)) {
      externalTransitionRef.current = true;
      setSelectedPeriod(availablePeriods[0]);
    }
  }, [period, externalPeriodChanged, availablePeriods, selectedPeriod]);

  const handleTabChange = (tabId: string) => {
    const nextPeriod = tabId as PeriodType;
    if (
      nextPeriod === selectedPeriod ||
      !availablePeriods.includes(nextPeriod)
    ) {
      return;
    }
    externalTransitionRef.current = false;
    setSelectedPeriod(nextPeriod);
    onPeriodChange?.(nextPeriod);
  };

  // Сохраняем даты в localStorage для текущего периода
  const saveDatesToStorage = useCallback(
    (from: string, to: string) => {
      const range = normalizeDates(from, to);
      if (range) {
        saveDatesForPeriod(
          storageKeys.dates,
          selectedPeriod,
          range.from,
          range.to
        );
      }
    },
    [selectedPeriod, storageKeys.dates, normalizeDates]
  );

  const applyPeriodToDate = useCallback(
    (selectedDateStr: string, period: PeriodType) => {
      const selectedDate = parseDateLocal(selectedDateStr);
      let fromDate: string = '';
      let toDate: string = '';

      if (period === 'day') {
        fromDate = formatDateLocal(selectedDate);
        toDate = formatDateLocal(selectedDate);
      } else if (period === 'week') {
        fromDate = formatDateLocal(selectedDate);
        const endDate = new Date(selectedDate);
        endDate.setDate(selectedDate.getDate() + 6);
        toDate = formatDateLocal(endDate);
      } else if (period === 'month') {
        selectedDate.setDate(1);
        fromDate = formatDateLocal(selectedDate);
        const endDate = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth() + 1,
          0
        );
        toDate = formatDateLocal(endDate);
      }

      // Ограничиваем конец периода последней доступной датой.
      const range = normalizeDates(fromDate, toDate);
      if (!range) return;
      pendingDatesRef.current = range;
      onDateChange(range.from, range.to);
      saveDatesForPeriod(storageKeys.dates, period, range.from, range.to);
    },
    [onDateChange, storageKeys.dates, normalizeDates]
  );

  const handleDateChangeWithSave = useCallback(
    (from: string, to: string) => {
      const range = normalizeDates(from, to);
      if (!range) return;
      pendingDatesRef.current = range;
      onDateChange(range.from, range.to);
      saveDatesToStorage(range.from, range.to);
    },
    [onDateChange, saveDatesToStorage, normalizeDates]
  );

  // Инициализация: проверяем сохраненные даты для текущего периода
  useEffect(() => {
    if (!loadSavedDates) {
      initializedStorageRef.current = null;
      return;
    }
    if (initializedStorageRef.current === storageKeys.dates) return;
    initializedStorageRef.current = storageKeys.dates;

    const savedDates = getSavedDatesForPeriod(
      storageKeys.dates,
      selectedPeriod
    );

    if (savedDates) {
      // Если есть сохраненные даты для текущего периода, используем их
      // Проверяем, отличаются ли они от переданных дат
      if (
        !dateFrom ||
        !dateTo ||
        dateFrom !== savedDates.from ||
        dateTo !== savedDates.to
      ) {
        handleDateChangeWithSave(savedDates.from, savedDates.to);
      }
    } else if (!dateFrom || !dateTo) {
      // Если нет сохраненных дат и даты не переданы, используем дефолтные
      if (selectedPeriod === 'day') {
        const today = new Date();
        if (excludeToday) {
          today.setDate(today.getDate() - 1);
        }
        const todayStr = formatDateLocal(today);
        applyPeriodToDate(todayStr, selectedPeriod);
      } else if (selectedPeriod === 'week') {
        const currentWeek = getCurrentWeek(excludeToday);
        handleDateChangeWithSave(currentWeek.from, currentWeek.to);
      } else if (selectedPeriod === 'month') {
        const today = new Date();
        if (excludeToday) {
          today.setDate(today.getDate() - 1);
        }
        const todayStr = formatDateLocal(today);
        applyPeriodToDate(todayStr, selectedPeriod);
      }
      // Для 'custom' ничего не делаем, если нет сохраненных дат
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKeys.dates, loadSavedDates]); // Только при монтировании

  const formatInputValue = useCallback(() => {
    if (!dateFrom || !dateTo) {
      if (selectedPeriod === 'custom') {
        return 'Выберите диапазон дат';
      }
      if (selectedPeriod === 'day') {
        return 'Выберите день';
      }
      return selectedPeriod === 'month'
        ? 'Выберите месяц'
        : 'Выберите дату для недели';
    }

    const fromDate = parseDateLocal(dateFrom);
    const toDate = parseDateLocal(dateTo);

    const formatMonth = (date: Date) => {
      return date.toLocaleDateString('ru-RU', { month: 'short' });
    };

    if (selectedPeriod === 'day') {
      const dayMonth = formatMonth(fromDate);
      return `${fromDate.getDate()} ${dayMonth}`;
    } else if (selectedPeriod === 'month') {
      const fromMonth = formatMonth(fromDate);
      const toMonth = formatMonth(toDate);
      return `Месяц: ${fromDate.getDate()} ${fromMonth} — ${toDate.getDate()} ${toMonth}`;
    } else if (selectedPeriod === 'week') {
      const fromMonth = formatMonth(fromDate);
      const toMonth = formatMonth(toDate);
      return `Неделя: ${fromDate.getDate()} ${fromMonth} — ${toDate.getDate()} ${toMonth}`;
    } else {
      const fromMonth = formatMonth(fromDate);
      const toMonth = formatMonth(toDate);
      return `${fromDate.getDate()} ${fromMonth} — ${toDate.getDate()} ${toMonth}`;
    }
  }, [dateFrom, dateTo, selectedPeriod]);

  // Даты и storage обновляются только после изменения внутреннего периода.
  useEffect(() => {
    if (
      externalPeriodChanged &&
      period &&
      availablePeriods.includes(period) &&
      period !== selectedPeriod
    ) {
      return;
    }
    const previousPeriod = appliedPeriodRef.current;
    if (previousPeriod === selectedPeriod) {
      const pending = pendingDatesRef.current;
      const changed =
        dateFrom !== appliedDatesRef.current.from ||
        dateTo !== appliedDatesRef.current.to;
      if (
        !pending ||
        changed ||
        (dateFrom === pending.from && dateTo === pending.to)
      ) {
        pendingDatesRef.current = null;
        saveDatesToStorage(dateFrom, dateTo);
        const range = normalizeDates(dateFrom, dateTo);
        if (range && (range.from !== dateFrom || range.to !== dateTo)) {
          handleDateChangeWithSave(range.from, range.to);
        }
      }
      appliedDatesRef.current = { from: dateFrom, to: dateTo };
      try {
        localStorage.setItem(storageKeys.period, selectedPeriod);
      } catch {
        // Storage may be unavailable.
      }
      return;
    }
    appliedPeriodRef.current = selectedPeriod;

    const previousDates = appliedDatesRef.current;
    const receivedNewDates =
      dateFrom !== previousDates.from || dateTo !== previousDates.to;
    const previousRange = normalizeDates(previousDates.from, previousDates.to);
    if (previousRange) {
      saveDatesForPeriod(
        storageKeys.dates,
        previousPeriod,
        previousRange.from,
        previousRange.to
      );
    }
    appliedDatesRef.current = { from: dateFrom, to: dateTo };
    const useExternalDates =
      externalTransitionRef.current && receivedNewDates && dateFrom && dateTo;
    externalTransitionRef.current = false;
    if (useExternalDates) {
      saveDatesToStorage(dateFrom, dateTo);
      const range = normalizeDates(dateFrom, dateTo);
      if (range && (range.from !== dateFrom || range.to !== dateTo)) {
        handleDateChangeWithSave(range.from, range.to);
      }
    }
    try {
      localStorage.setItem(storageKeys.period, selectedPeriod);
    } catch (e) {
      console.warn('Failed to save period to localStorage', e);
    }

    // Родитель уже пересчитал диапазон вместе с периодом: не перезаписываем его.
    if (useExternalDates) return;

    const savedDates = getSavedDatesForPeriod(
      storageKeys.dates,
      selectedPeriod
    );
    if (savedDates) {
      handleDateChangeWithSave(savedDates.from, savedDates.to);
    } else if (selectedPeriod === 'week') {
      const currentWeek = getCurrentWeek(excludeToday);
      handleDateChangeWithSave(currentWeek.from, currentWeek.to);
    } else if (selectedPeriod === 'day' || selectedPeriod === 'month') {
      const today = new Date();
      if (excludeToday) {
        today.setDate(today.getDate() - 1);
      }
      applyPeriodToDate(formatDateLocal(today), selectedPeriod);
    } else {
      saveDatesToStorage(dateFrom, dateTo);
    }
    // Для custom без сохраненных дат сохраняем текущий диапазон.
  }, [
    selectedPeriod,
    externalPeriodChanged,
    period,
    availablePeriods,
    dateFrom,
    dateTo,
    storageKeys.dates,
    storageKeys.period,
    excludeToday,
    handleDateChangeWithSave,
    applyPeriodToDate,
    saveDatesToStorage,
    normalizeDates,
  ]);

  useEffect(() => {
    if (!inputRef.current) return;

    if (flatpickrRef.current) {
      flatpickrRef.current.destroy();
    }

    const isCustom = selectedPeriod === 'custom';
    const isMonth = selectedPeriod === 'month';
    const isSingleDate =
      selectedPeriod === 'day' ||
      selectedPeriod === 'week' ||
      selectedPeriod === 'month';
    const defaultDates =
      isCustom && dateFrom && dateTo
        ? [parseDateLocal(dateFrom), parseDateLocal(dateTo)]
        : dateFrom
          ? [parseDateLocal(dateFrom)]
          : undefined;

    const maxDate = allowFuture
      ? undefined
      : excludeToday
        ? (() => {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return formatDateLocal(yesterday);
          })()
        : 'today';

    const minDate = allowPast ? undefined : allowFuture ? 'today' : undefined;

    flatpickrRef.current = flatpickr(inputRef.current, {
      mode: isSingleDate ? 'single' : 'range',
      dateFormat: 'Y-m-d',
      locale: Russian,
      defaultDate: defaultDates,
      ...(maxDate ? { maxDate } : {}),
      ...(minDate ? { minDate } : {}),
      allowInput: false,
      disableMobile: true,
      onChange: selectedDates => {
        if (isCustom) {
          if (selectedDates.length === 2) {
            const fromDate = formatDateLocal(selectedDates[0]);
            const toDate = formatDateLocal(selectedDates[1]);
            handleDateChangeWithSave(fromDate, toDate);
          }
        } else {
          if (selectedDates.length === 1) {
            const selectedDate = formatDateLocal(selectedDates[0]);
            applyPeriodToDate(selectedDate, selectedPeriod);
          }
        }
      },
      onReady: (_selectedDates, _dateStr, instance) => {
        if (isMonth) {
          instance.calendarContainer.classList.add('sphere-month-picker');
          if (instance.innerContainer) instance.innerContainer.hidden = true;
          instance.monthsDropdownContainer.tabIndex = 0;
          instance.currentYearElement.tabIndex = 0;
          instance._bind(instance.monthNav, 'keydown', event => {
            if (event.key !== 'Escape') event.stopPropagation();
          });

          let lastSelectedMonth: string | null = null;
          instance._bind(
            instance.monthNav,
            ['change', 'click', 'keyup', 'increment'],
            event => {
              const target = event.target as HTMLElement;
              const isYear = target === instance.currentYearElement;
              const changedMonth =
                event.type === 'change' &&
                target === instance.monthsDropdownContainer;
              const changedYear =
                isYear && ['change', 'keyup', 'increment'].includes(event.type);
              const clickedArrow =
                event.type === 'click' &&
                target.closest(
                  '.flatpickr-prev-month, .flatpickr-next-month, .numInputWrapper .arrowUp, .numInputWrapper .arrowDown'
                );
              if (!changedMonth && !changedYear && !clickedArrow) return;

              if (isYear && event.type === 'change') {
                instance.changeYear(Number(instance.currentYearElement.value));
              }
              if (
                isYear &&
                instance.currentYearElement.value !==
                  String(instance.currentYear)
              ) {
                return;
              }

              const month = `${instance.currentYear}-${instance.currentMonth}`;
              if (month === lastSelectedMonth) return;
              lastSelectedMonth = month;
              let date = new Date(
                instance.currentYear,
                instance.currentMonth,
                1
              );
              if (instance.config.minDate && date < instance.config.minDate) {
                date = instance.config.minDate;
              }
              instance.setDate(date, true);
            }
          );
        }
        if (inputRef.current) {
          inputRef.current.value = formatInputValue();
        }
      },
      onClose: selectedDates => {
        if (isCustom && selectedDates.length === 1) {
          const date = formatDateLocal(selectedDates[0]);
          handleDateChangeWithSave(date, date);
        }
        if (inputRef.current) {
          inputRef.current.value = formatInputValue();
        }
      },
    });

    if (inputRef.current) {
      inputRef.current.value = formatInputValue();
    }

    return () => {
      if (flatpickrRef.current) {
        flatpickrRef.current.destroy();
        flatpickrRef.current = null;
      }
    };
  }, [
    allowFuture,
    allowPast,
    excludeToday,
    selectedPeriod,
    dateFrom,
    dateTo,
    onDateChange,
    applyPeriodToDate,
    formatInputValue,
    handleDateChangeWithSave,
  ]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = formatInputValue();
    }
  }, [dateFrom, dateTo, selectedPeriod, formatInputValue]);

  const tabs = availablePeriods.map(period => ({
    id: period,
    label: PERIOD_LABELS[period],
  }));

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <Tabs
          tabs={tabs}
          activeTab={selectedPeriod}
          onChange={handleTabChange}
          variant="default"
          size="md"
          className={styles.tabs}
        />

        <div
          className={styles.inputContainer}
          onClick={() => {
            if (flatpickrRef.current) {
              flatpickrRef.current.open();
            }
          }}
        >
          <input
            ref={inputRef}
            type="text"
            placeholder={
              selectedPeriod === 'custom'
                ? 'Выберите диапазон дат'
                : selectedPeriod === 'day'
                  ? 'Выберите день'
                  : selectedPeriod === 'month'
                    ? 'Выберите месяц'
                    : 'Выберите дату для недели'
            }
            className={styles.input}
            readOnly
            onClick={() => {
              if (flatpickrRef.current) {
                flatpickrRef.current.open();
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
